import React from 'react';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm1_u1_x8_volumenstromregelung';

const M1_U1_X8_Volumenstromregelung: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const textBlocks = React.useMemo(() => ([
    { id: 'block_titel', heading: 'Volumenstromregelung am Lüftungsgerät', text: override?.texts?.['block_titel'] ?? 'Geräte benötigen präzise Volumenstromregelung für Komfort, Hygiene und Energieeffizienz.' },
    { id: 'block_hauptkomponenten', heading: 'Hauptkomponenten', text: override?.texts?.['block_hauptkomponenten'] ?? 'Ventilator (EC/FU), Drucksensoren, interne Steuerung (DDC/SPS), optionale Sensoren (CO2, VOC, Präsenz).' },
    { id: 'block_wirkdruckmessung', heading: 'Wirkdruckmessung', text: override?.texts?.['block_wirkdruckmessung'] ?? 'V̇ ≈ k * √Δp. Δp messen, Volumenstrom über Kennlinie. Vorteile: kostengünstig. Nachteile: indirekt, abhängig von Verschmutzung.' },
    { id: 'block_weitere_methoden', heading: 'Weitere Methoden', text: override?.texts?.['block_weitere_methoden'] ?? 'Flügelrad-, Hitzdraht-, Ultraschall-Sensoren, Messblenden, indirekt über EC-Motorleistung.' },
    { id: 'block_umsetzung', heading: 'Umsetzung', text: override?.texts?.['block_umsetzung'] ?? 'Regler vergleicht Soll/Ist und passt Drehzahl via PI(D) an.' },
    { id: 'block_einsatz', heading: 'Einsatz', text: override?.texts?.['block_einsatz'] ?? 'CAV: Konstanter Bedarf; kompensiert Filter. VAV: Bedarfsgerechte Anpassung für Effizienz und Komfort.' }
  ]), [override]);

  const fullPlainText = React.useMemo(() => {
    return textBlocks.map(b => `${b.heading}. ${b.text}`).join(' ');
  }, [textBlocks]);

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canUploadImagesM1) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImagesM1]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canEditTextsM1) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTextsM1]);

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(
    UnitPageShell,
    { title: '1.1.8 Volumenstromregelung', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        textBlocks.slice(0, 2).map((block) => React.createElement(EditableTextBlock, {
          key: block.id,
          pageId: PAGE_ID,
          blockId: block.id,
          heading: block.heading,
          body: block.text,
          onSave: (newBody: string) => handleTextSave(block.id, newBody)
        })),
        React.createElement(ZoomableImage, {
          src: (() => { const v = override?.images?.['img_0']; if (!v) return '/Bilder/luftung_lueftung_volumenstrom.jpg'; const s = String(v); return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`; })(),
          alt: 'Schema Lüftungsgerät Komponenten',
          label: 'Bild einfügen',
          onReplace: canUploadImagesM1 ? (file: File) => handleImageReplace('img_0', file) : undefined
        })
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        textBlocks.slice(2, 3).map((block) => React.createElement(EditableTextBlock, {
          key: block.id,
          pageId: PAGE_ID,
          blockId: block.id,
          heading: block.heading,
          body: block.text,
          onSave: (newBody: string) => handleTextSave(block.id, newBody)
        })),
        React.createElement(ZoomableImage, {
          src: (() => { const v = override?.images?.['img_1']; if (!v) return '/Bilder/lueftung_lueftung_wirkdruckmessung_visu_klein.PNG'; const s = String(v); return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`; })(),
          alt: 'Wirkdruckmessung Visualisierung',
          label: 'Bild einfügen',
          onReplace: canUploadImagesM1 ? (file: File) => handleImageReplace('img_1', file) : undefined
        }),
        textBlocks.slice(3).map((block) => React.createElement(EditableTextBlock, {
          key: block.id,
          pageId: PAGE_ID,
          blockId: block.id,
          heading: block.heading,
          body: block.text,
          onSave: (newBody: string) => handleTextSave(block.id, newBody)
        })),
        React.createElement(Box, { sx: { mt: 3 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m1_u1_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M1_U1_X8_Volumenstromregelung;

